function queryAllMediaCardBlockIds(root: ParentNode): Set<string> {
  const ids = new Set<string>();
  for (const el of Array.from(root.querySelectorAll("[data-media-card-info-panel]"))) {
    const id = el.getAttribute("data-media-card-info-panel");
    if (id) ids.add(id);
  }
  for (const el of Array.from(root.querySelectorAll("[data-media-card-info-overlay]"))) {
    const id = el.getAttribute("data-media-card-info-overlay");
    if (id) ids.add(id);
  }
  return ids;
}

function closeMediaCardInfoBlock(root: ParentNode, blockId: string, animated: boolean) {
  const desktopPanel = root.querySelector<HTMLElement>(`[data-media-card-info-panel="${blockId}"]`);
  const mobileOverlay = root.querySelector<HTMLElement>(`[data-media-card-info-overlay="${blockId}"]`);
  const mobileSheet = mobileOverlay?.querySelector<HTMLElement>("[data-media-card-info-sheet]");
  const mobileBackdrop = mobileOverlay?.querySelector<HTMLElement>("[data-media-card-info-backdrop]");

  if (desktopPanel) {
    desktopPanel.classList.remove("opacity-100", "scale-100", "pointer-events-auto");
    desktopPanel.classList.add("opacity-0", "scale-95", "pointer-events-none");
  }

  if (!mobileOverlay || !mobileSheet || !mobileBackdrop) return;

  mobileBackdrop.classList.remove("opacity-100");
  mobileBackdrop.classList.add("opacity-0");
  mobileSheet.classList.remove("opacity-100", "scale-100");
  mobileSheet.classList.add("opacity-0", "scale-95");
  document.body.style.overflow = "";

  if (animated) {
    mobileSheet.addEventListener(
      "transitionend",
      () => {
        mobileOverlay.classList.add("pointer-events-none");
        mobileOverlay.setAttribute("aria-hidden", "true");
      },
      { once: true },
    );
  } else {
    mobileOverlay.classList.add("pointer-events-none");
    mobileOverlay.setAttribute("aria-hidden", "true");
  }
}

function closeAllMediaCardInfoExcept(root: ParentNode, exceptBlockId: string) {
  for (const id of queryAllMediaCardBlockIds(root)) {
    if (id === exceptBlockId) continue;
    closeMediaCardInfoBlock(root, id, false);
  }
}

export function initMediaCardInfo(scope: ParentNode = document) {
  const triggers = scope.querySelectorAll<HTMLButtonElement>("[data-media-card-info-trigger]");

  for (const trigger of Array.from(triggers)) {
    const blockId = trigger.dataset.blockId;
    if (!blockId) continue;

    if (trigger.dataset.infoInit === "true") continue;
    trigger.dataset.infoInit = "true";

    const desktopPanel = scope.querySelector<HTMLElement>(`[data-media-card-info-panel="${blockId}"]`);
    const mobileOverlay = scope.querySelector<HTMLElement>(`[data-media-card-info-overlay="${blockId}"]`);
    const mobileSheet = mobileOverlay?.querySelector<HTMLElement>("[data-media-card-info-sheet]");
    const mobileBackdrop = mobileOverlay?.querySelector<HTMLElement>("[data-media-card-info-backdrop]");
    const closeButtons = scope.querySelectorAll<HTMLButtonElement>(`[data-media-card-info-close="${blockId}"]`);

    const isMobile = () => window.innerWidth < 1024;

    function openMobile() {
      if (!mobileOverlay || !mobileSheet || !mobileBackdrop) return;
      mobileOverlay.classList.remove("pointer-events-none");
      mobileOverlay.removeAttribute("aria-hidden");
      document.body.style.overflow = "hidden";
      requestAnimationFrame(() => {
        mobileBackdrop.classList.remove("opacity-0");
        mobileBackdrop.classList.add("opacity-100");
        mobileSheet.classList.remove("opacity-0", "scale-95");
        mobileSheet.classList.add("opacity-100", "scale-100");
      });
    }

    function closeMobile() {
      closeMediaCardInfoBlock(document, blockId, true);
    }

    function openDesktop() {
      if (!desktopPanel) return;
      requestAnimationFrame(() => {
        desktopPanel.classList.remove("opacity-0", "scale-95", "pointer-events-none");
        desktopPanel.classList.add("opacity-100", "scale-100", "pointer-events-auto");
      });
    }

    function closeDesktop() {
      if (!desktopPanel) return;
      desktopPanel.classList.remove("opacity-100", "scale-100", "pointer-events-auto");
      desktopPanel.classList.add("opacity-0", "scale-95", "pointer-events-none");
    }

    trigger.addEventListener("click", () => {
      closeAllMediaCardInfoExcept(document, blockId);
      if (isMobile()) {
        openMobile();
      } else {
        openDesktop();
      }
    });

    for (const btn of Array.from(closeButtons)) {
      btn.addEventListener("click", () => {
        if (isMobile()) closeMobile();
        else closeDesktop();
      });
    }

    mobileBackdrop?.addEventListener("click", () => closeMobile());
  }
}

const runOnReady = (cb: () => void) => {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", cb);
  } else {
    cb();
  }
};

runOnReady(() => initMediaCardInfo());
document.addEventListener("shopify:section:load", (e) => {
  const target = (e.target as ParentNode) || document;
  initMediaCardInfo(target);
});
